import { API_URL } from '../config/apiConfig';

export interface ImageEditRequest {
  imageFile?: File;
  assetUrl?: string;
  prompt: string;
  model: string;
}

export interface ImageEditResponse {
  jobId: string;
}

export interface JobStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  imageUrl?: string;
  error?: string;
}

/**
 * Mock image editing using Canvas API
 * Applies a subtle watermark and contrast adjustment to simulate editing
 */
export async function mockEditImage(
  imageUrl: string,
  onProgress: (progress: number) => void
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Draw original image
      ctx.drawImage(img, 0, 0);

      // Apply subtle edits
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Increase contrast slightly
      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.min(255, data[i] * 1.1); // Red
        data[i + 1] = Math.min(255, data[i + 1] * 1.1); // Green
        data[i + 2] = Math.min(255, data[i + 2] * 1.1); // Blue
      }

      ctx.putImageData(imageData, 0, 0);

      // Add subtle "Edited" watermark
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.font = '24px Arial';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText('Edited', canvas.width - 20, canvas.height - 20);

      // Simulate progress
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 25;
        onProgress(Math.min(progress, 100));
        if (progress >= 100) {
          clearInterval(progressInterval);
          // Convert to blob URL
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const url = URL.createObjectURL(blob);
                resolve(url);
              } else {
                reject(new Error('Failed to create blob'));
              }
            },
            'image/png',
            0.95
          );
        }
      }, 500); // 2 seconds total (4 intervals of 500ms)
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = imageUrl;
  });
}

/**
 * Start an image editing job
 */
export async function startImageEditJob(
  request: ImageEditRequest
): Promise<ImageEditResponse> {
  const API_BASE_URL = API_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_URL is not configured.');
  }

  const formData = new FormData();
  if (request.imageFile) {
    formData.append('image', request.imageFile);
  }
  if (request.assetUrl) {
    formData.append('assetUrl', request.assetUrl);
  }
  formData.append('prompt', request.prompt);
  formData.append('model', request.model);

  const response = await fetch(`${API_BASE_URL}/v1/image/edit`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to start job' }));
    throw new Error(error.message || 'Failed to start image editing job');
  }

  return await response.json();
}

/**
 * Poll job status
 */
export async function pollEditJob(jobId: string): Promise<JobStatus> {
  const API_BASE_URL = API_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_URL is not configured.');
  }

  const response = await fetch(`${API_BASE_URL}/v1/image/jobs/${jobId}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch job status' }));
    throw new Error(error.message || 'Failed to fetch job status');
  }

  return await response.json();
}

